import { useContext, useMemo, useState } from 'react';

import { mockFn } from 'src/tools/fn.tools';
import { useFuzzyFinder } from 'src/hooks/useFuzzyFinder';

import { ButtonBaseType, ID, Maybe, RecordObject } from 'src/models';
import { InputContext } from 'src/contexts/input.context';

import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InputWithIcons } from 'src/components/Inputs';
import { BaseComponentProps } from 'src/components/Dropdown';
import { t } from 'src/tools/text.tools';
import { ifTrue } from 'src/tools/logic.tools';
import { P1, P4 } from 'src/components/Typography';

import classes from './SearchableNestedDropdown.module.scss';
import { SEARCH_KEYS } from 'src/data/misc.data';

const TXT = t(['dropdown']);

export interface SearchableNestedDropdownInputProps extends BaseComponentProps {
  value?: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onDiscard: () => void;
}

export interface SearchableNestedDropdownProps {
  items?: ButtonBaseType[];
  nestedItems?: RecordObject<ButtonBaseType[]>;
  isSearchable?: boolean;
  renderInput?: (props: SearchableNestedDropdownInputProps) => JSX.Element;
  renderItemLabel?: (item: ButtonBaseType, isSelected: boolean, isActive: boolean) => string | JSX.Element;
  isStrictSearch?: boolean;
  isDropdownAutoOpen?: boolean;
}

export function SearchableNestedDropdown({
  items: propsItems,
  nestedItems: propsNestedItems,
  isSearchable,
  renderInput,
  isStrictSearch,
  isDropdownAutoOpen,
}: SearchableNestedDropdownProps) {
  const {
    value,
    data: visibleOnlyItems,
    onChange,
    classNames,
    onDiscard = mockFn,
    isDisabled,
    isReadonly,
    isAutoFocus,
    onClick,
    customization = {},
    placeholder,
    items: contextItems,
    nestedItems: contextNestedItems,
    actions = {},
  } = useContext(InputContext);

  const { onChangeSection } = actions;

  const items = useMemo(() => {
    const allItems = propsItems ?? contextItems ?? [];

    if (!visibleOnlyItems?.length) return allItems;

    return allItems.filter(i => visibleOnlyItems.includes(i.id));
  }, [contextItems, propsItems, visibleOnlyItems]);

  const nestedItems = useMemo(
    () => propsNestedItems ?? contextNestedItems ?? {},
    [contextNestedItems, propsNestedItems],
  );

  const { makeArrayDataToRender } = useFuzzyFinder(SEARCH_KEYS, isStrictSearch);

  const [search, setSearch] = useState<Maybe<string>>();

  const itemsToRender = useMemo(() => {
    let result: ButtonBaseType[] = [];

    items.forEach(item => {
      const subItems = makeArrayDataToRender(search, nestedItems[item.id] ?? []);

      if (subItems.length) {
        result = [
          ...result,
          {
            ...item,
            isDisabled: true,
            className: classes['header-item'],
          },
          ...subItems,
        ];
      }
    });

    return result;
  }, [items, makeArrayDataToRender, nestedItems, search]);

  const { dropdownMinWidth = 'container-width', isSmallMenu, isInline, dropdownPosition } = customization;

  const currentItem = useMemo(
    () =>
      value && itemsToRender.find(i => (typeof value === 'string' ? String(i.id) === value : Number(i.id) === value)),
    [value, itemsToRender],
  );

  const [isOpen, setIsOpen] = useState(false);

  const isItemsNotAvailable = !isDisabled && !isReadonly && !itemsToRender.length;

  function getItemSectionId(itemId: ID) {
    return Object.keys(nestedItems).find(sectionId => !!nestedItems[sectionId].find(i => i.id === itemId));
  }

  function handleOnItemClick(id: ID) {
    onChange(String(id), true);
    onChangeSection?.(getItemSectionId(id));
    setSearch(undefined);
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      if (isReadonly) return;

      fn();

      if (isDropdownAutoOpen) setIsOpen(true);

      if (onClick) onClick();
    };
  }

  function handleOnChange(val: string) {
    setSearch(val);
    setIsOpen(true);
  }

  function handleOnRequestOpenClose(state: boolean) {
    setIsOpen(state);

    if (!state) {
      onDiscard();
      setSearch(undefined);
    }
  }

  function handleOnDiscard() {
    handleOnRequestOpenClose(false);
  }

  function handleOnFocus(fn: () => void) {
    return function call() {
      if (isReadonly) return;

      fn();

      handleOnRequestOpenClose(true);
    };
  }

  function renderItemLabel(item: ButtonBaseType) {
    if (item.isDisabled) {
      const P = isSmallMenu ? P4 : P1;

      return (
        <P key={item.id} title={item.label} className='t-ellipsis c-default'>
          {item.label}
        </P>
      );
    }

    return item.label;
  }

  function renderBaseComponent(baseComponentProps: BaseComponentProps) {
    const extendedProps = {
      value: search
        ? search
        : currentItem?.label
        ? currentItem?.sub_label
          ? currentItem?.sub_label + currentItem?.label
          : currentItem?.label
        : value,
      onDiscard: handleOnDiscard,
      onSubmit: handleOnDiscard,
      onChange: handleOnChange,
      onClick: getHandleOnClick(baseComponentProps.onClick),
      onFocus: isSearchable
        ? baseComponentProps.onFocus
        : isDropdownAutoOpen
        ? handleOnFocus(baseComponentProps.onFocus)
        : mockFn,
    };

    if (renderInput)
      return renderInput({
        ...baseComponentProps,
        ...extendedProps,
      });

    return (
      <InputWithIcons
        {...extendedProps}
        className={classNames?.input}
        onBlur={baseComponentProps.onBlur}
        isDisabled={isDisabled || !itemsToRender.length}
        autoFocus={isAutoFocus}
        rightIconId={baseComponentProps.isOpen ? 'chevron-up' : 'chevron-down'}
        readOnly={isReadonly || !isSearchable}
        placeholder={placeholder}
        isSmall={customization.isSmall}
        isXSmall={customization.isXSmall}
      />
    );
  }

  return (
    <DropdownMenu
      tooltip={ifTrue(isItemsNotAvailable, TXT('dropdownValuesIsNotAvailable'))}
      classNames={classNames}
      items={itemsToRender}
      currentItemId={value}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      renderItemLabel={renderItemLabel}
      dropdownMinWidth={dropdownMinWidth}
      isOpen={isOpen}
      onRequestOpenClose={handleOnRequestOpenClose}
      isSmall={isSmallMenu}
      isInline={isInline}
      position={dropdownPosition}
    />
  );
}
